import {
  type SessionData,
  type RequestToken,
  type AbortVariant,
} from '../types';
import { isInIframe } from '../utils/iframe';

// -- State machine --

export type AppState =
  | { type: 'landing' }
  | { type: 'loading' }
  | { type: 'appInstall'; appName: string }
  | { type: 'error'; variant: AbortVariant; canLeave: boolean }
  | { type: 'mobileFlowStart'; session: SessionData }
  | { type: 'appSelect'; session: SessionData }
  | { type: 'chulak'; session: SessionData; selectedToken: RequestToken };

export type AppAction =
  | {
      action: 'INIT_SUCCESS';
      session: SessionData;
      isMobile: boolean;
    }
  | { action: 'INIT_ERROR'; variant: AbortVariant; canLeave?: boolean }
  | { action: 'MOBILE_CONTINUE' }
  | { action: 'SELECT_TOKEN'; token: RequestToken }
  | { action: 'BACK_TO_PICKER' };

export function appReducer(state: AppState, action: AppAction): AppState {
  switch (action.action) {
    case 'INIT_SUCCESS': {
      const { session, isMobile } = action;
      if (isMobile) return { type: 'mobileFlowStart', session };
      return startOrPick(session);
    }
    case 'INIT_ERROR':
      return {
        type: 'error',
        variant: action.variant,
        // Errors where no client redirect is possible hide the "Leave" action.
        canLeave: action.canLeave ?? true,
      };
    case 'MOBILE_CONTINUE': {
      if (state.type !== 'mobileFlowStart') return state;
      return startOrPick(state.session);
    }
    case 'SELECT_TOKEN': {
      if (state.type !== 'appSelect') return state;
      return {
        type: 'chulak',
        session: state.session,
        selectedToken: action.token,
      };
    }
    case 'BACK_TO_PICKER': {
      if (state.type !== 'chulak') return state;
      return { type: 'appSelect', session: state.session };
    }
  }
}

/**
 * The request offers one app. Then there is no app list: the flow starts with
 * that app, and nothing offers a way back to the list.
 */
export function isSingleApp(session: SessionData): boolean {
  return session.tokens.length === 1;
}

/** Start with the only app, or show the app list. */
function startOrPick(session: SessionData): AppState {
  if (isSingleApp(session)) {
    return { type: 'chulak', session, selectedToken: session.tokens[0] };
  }
  return { type: 'appSelect', session };
}

// -- Init --

export function getInitialState(): AppState {
  const searchParams = new URLSearchParams(window.location.search);
  const appInstallParam = searchParams.get('app');
  if (appInstallParam) return { type: 'appInstall', appName: appInstallParam };
  if (searchParams.has('oauth_interaction_start_uri'))
    return { type: 'loading' };
  // Embedded mode: start in loading and wait for the parent's ronan:init.
  if (isInIframe()) return { type: 'loading' };
  return { type: 'landing' };
}
